import User from 'auth/User';
import React, { useState } from 'react';
import { PAGES_ENUM, PERMISSION_ENUM } from 'routes/enums';
import permissionService from 'routes/permissionService';
import { Table } from 'screens/Panel/components/Table/Table.styled';
import { updateChecklist } from 'screens/Panel/screens/Firm/api/firm';
import GeneralComments from 'screens/Panel/screens/Firm/components/DueDiligence/Checklist/GeneralComments';
import { DueDiligenceChecklist } from 'utils/api/types';
import { Nullable } from 'utils/types';
import { FIELD_LABELS, FIELD_NAMES, POLICY_ANSWERS, POLICY_ANSWERS_TEXT } from './types';

const formatYesNo = (value: Nullable<string>) => {
  if (value == null || !Object.keys(POLICY_ANSWERS_TEXT).includes(value)) {
    return '';
  }

  return POLICY_ANSWERS_TEXT[value as POLICY_ANSWERS];
};

const Checklist: React.FC<{
  firmId: string;
  checklist: DueDiligenceChecklist;
  user: User;
  reloadFirm: () => void;
}> = ({ firmId, checklist, user, reloadFirm }) => {
  const hasFirstLinePermission = permissionService.hasPermission(
    user,
    PAGES_ENUM.ORGANIZATION_DETAILS,
    PERMISSION_ENUM.DUE_DILIGENCE_FIRST_LINE,
  );
  const hasSecondLinePermission = permissionService.hasPermission(
    user,
    PAGES_ENUM.ORGANIZATION_DETAILS,
    PERMISSION_ENUM.DUE_DILIGENCE_SECOND_LINE,
  );
  const [isCommentSaving, setCommentSaving] = useState<boolean>(false);

  const handleCommentChange = async (comment: string) => {
    setCommentSaving(true);
    try {
      await updateChecklist(firmId, {
        [FIELD_NAMES.GENERAL_COMMENTS]: comment,
      });
      reloadFirm();
    } catch (e) {}
    setCommentSaving(false);
  };

  return (
    <div>
      <Table>
        <thead>
          <tr>
            <th style={{ width: '50%' }}>Question</th>
            <th>Answer</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{FIELD_LABELS.COMPANIES_HOUSE_NUMBER}</td>
            <td>{checklist[FIELD_NAMES.COMPANIES_HOUSE_NUMBER]}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.MANAGEMENT_DOB}</td>
            <td>{checklist[FIELD_NAMES.MANAGEMENT_DOB]}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.ICO_PROTECTION_NUMBER}</td>
            <td>{checklist[FIELD_NAMES.ICO_PROTECTION_NUMBER]}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.NUMBER_OF_COMPLAINTS_RECEIVED}</td>
            <td>{checklist[FIELD_NAMES.NUMBER_OF_COMPLAINTS_RECEIVED]}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.NUMBER_OF_COMPLAINTS_UPHELD}</td>
            <td>{checklist[FIELD_NAMES.NUMBER_OF_COMPLAINTS_UPHELD]}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.WHO_CARRIES_COMPLIANCE_OVERSIGHT}</td>
            <td>{checklist[FIELD_NAMES.WHO_CARRIES_COMPLIANCE_OVERSIGHT]}</td>
          </tr>
        </tbody>
        <thead>
          <tr>
            <th colSpan={2}>Company policies</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{FIELD_LABELS.FINANCIAL_CRIME_POLICY}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.FINANCIAL_CRIME_POLICY])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.DATA_PROTECTION_POLICY}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.DATA_PROTECTION_POLICY])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.TREATING_CUSTOMER_FAIRLY_POLICY}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.TREATING_CUSTOMER_FAIRLY_POLICY])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.DISASTER_RECOVERY_POLICY}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.DISASTER_RECOVERY_POLICY])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.EMPLOYEE_ACCESS_REMOVAL_PROCESS}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.EMPLOYEE_ACCESS_REMOVAL_PROCESS])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.PII_IN_PLACE_WITH_FCA_REQUIREMENTS}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.PII_IN_PLACE_WITH_FCA_REQUIREMENTS])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.SENSITIVE_DATA_POLICY}</td>
            <td>{formatYesNo(checklist[FIELD_NAMES.SENSITIVE_DATA_POLICY])}</td>
          </tr>
          <tr>
            <td>{FIELD_LABELS.ADDITIONAL_INFORMATION}</td>
            <td>{checklist[FIELD_NAMES.ADDITIONAL_INFORMATION]}</td>
          </tr>
        </tbody>
      </Table>
      <GeneralComments
        comment={checklist[FIELD_NAMES.GENERAL_COMMENTS]}
        onChange={handleCommentChange}
        isSaving={isCommentSaving}
        disabled={!(hasFirstLinePermission || hasSecondLinePermission)}
      />
    </div>
  );
};

export default Checklist;
